import { firstListRecord } from "./lostSalesPageDefaults";

export const selectableSku = (value) => {
  if (!value || typeof value !== "object") return null;
  if (value.isNoData || value.isLoadMore) return null;
  const sku = typeof value.sku === "string" ? value.sku.trim() : "";
  return sku ? { sku } : null;
};

export const selectableWarehouse = (value) => {
  if (!value || typeof value !== "object") return null;
  if (value.isNoData || value.isLoadMore) return null;
  return value._id ? value : null;
};

export const pickDefaultWarehouse = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];
  const flagged = rows.find((row) => selectableWarehouse(row)?.default_warehouse);
  return selectableWarehouse(flagged) || selectableWarehouse(rows[0]);
};

export const pickDefaultSku = (response) =>
  selectableSku(firstListRecord(response));

/**
 * Keep the previously selected SKU only when it is still an exact eligible
 * option for the newly selected warehouse. Partial search hits are not enough.
 */
export const resolveSkuAfterWarehouseChange = ({
  previousSku,
  searchHits,
} = {}) => {
  const needle = String(previousSku || "").trim().toLowerCase();
  if (!needle) return null;
  const match = (Array.isArray(searchHits) ? searchHits : []).find((row) => {
    const selected = selectableSku(row);
    return selected && selected.sku.toLowerCase() === needle;
  });
  return selectableSku(match);
};

export const canRunInventoryHealth = ({
  clientId,
  warehouse,
  warehousesLoading,
}) =>
  Boolean(clientId && warehouse?._id && !warehousesLoading);

export const SNAPSHOT_UNAVAILABLE_CODES = new Set([
  "no_snapshots",
  "insufficient_snapshots",
]);

export const isAuditUnavailable = (payload) =>
  Boolean(
    payload &&
      (payload.available === false ||
        SNAPSHOT_UNAVAILABLE_CODES.has(payload.code)),
  );

export const auditUnavailableCopy = (payload) => {
  if (payload?.code === "insufficient_snapshots") {
    return {
      heading:
        payload.heading ||
        "AUDIT NOT AVAILABLE — INSUFFICIENT INVENTORY SNAPSHOTS",
      title:
        payload.title ||
        "Only one inventory snapshot was found for this warehouse.",
      body:
        payload.message ||
        "A beginning and ending snapshot are required to compare inventory over the audit period.",
    };
  }
  return {
    heading:
      payload?.heading || "AUDIT NOT AVAILABLE — NO INVENTORY SNAPSHOTS",
    title:
      payload?.title ||
      "No inventory snapshots were found for this warehouse.",
    body:
      payload?.message ||
      "The audit period is determined by historical inventory snapshots. At least two snapshot dates are required.",
  };
};

/**
 * Mutually exclusive render states for the Inventory Health page. A result is
 * only ever "fresh" for the warehouse it was requested for, so a pending or
 * superseded request can never fall back to the previous warehouse's outcome.
 */
export const AUDIT_VIEW = {
  NO_CLIENT: "no_client",
  NO_WAREHOUSE: "no_warehouse",
  LOADING: "loading",
  ERROR: "error",
  UNAVAILABLE: "unavailable",
  SUCCESS: "success",
};

export const resolveAuditViewState = ({
  clientId,
  warehouseId,
  warehousesLoading,
  warehousesEmpty,
  loading,
  error,
  payload,
  payloadWarehouseId,
} = {}) => {
  if (!clientId) return AUDIT_VIEW.NO_CLIENT;
  if (warehousesLoading) return AUDIT_VIEW.LOADING;
  if (error) return AUDIT_VIEW.ERROR;
  if (!warehouseId) return AUDIT_VIEW.NO_WAREHOUSE;
  if (loading) return AUDIT_VIEW.LOADING;
  const fresh = Boolean(payload) && payloadWarehouseId === warehouseId;
  if (!fresh) return AUDIT_VIEW.LOADING;
  return isAuditUnavailable(payload)
    ? AUDIT_VIEW.UNAVAILABLE
    : AUDIT_VIEW.SUCCESS;
};

export const auditEmptyState = (payload) => {
  if (!payload || isAuditUnavailable(payload)) return null;
  const positions = Number(
    payload.summary?.positions_audited ?? payload.summary?.sku_count ?? 0,
  );
  const missingData = Number(payload.summary?.missing_data ?? 0);
  if (positions === 0) {
    if (missingData > 0) {
      return {
        severity: "info",
        title: "No positions could be audited.",
        body: "Every assembled position is missing a starting or ending historical inventory snapshot.",
      };
    }
    return {
      severity: "info",
      title: "No auditable inventory positions found.",
      body: "Two inventory snapshots exist, but no SKU positions were found in snapshots, sales, purchase orders, or lots for this period.",
    };
  }
  if (!(payload.rows || []).length) {
    return {
      severity: "success",
      title: "No reconciliation discrepancies found.",
      body: "Every audited position had Delta = 0 and is hidden from the exception table.",
    };
  }
  return null;
};
